import { parseApiError, parseFieldErrors } from "../utils/apiErrors";

export const API_BASE = (typeof window !== "undefined" && /^\d+\.\d+\.\d+\.\d+$/.test(window.location?.hostname || "") ? window.location.origin : (process.env.REACT_APP_API_BASE || "http://191.101.70.92:8001"));

export function setToken(token) {
  localStorage.setItem("token", token);
}

export function getToken() {
  return localStorage.getItem("token");
}

export function clearToken() {
  localStorage.removeItem("token");
}

export async function login(username, password) {
  let response;

  try {
    const corpo = new URLSearchParams();
    corpo.set("username", username);
    corpo.set("password", password);
    response = await fetch(`${API_BASE}/token`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: corpo,
    });
  } catch (error) {
    const isHttpsPage =
      typeof window !== "undefined" && window.location?.protocol === "https:";
    const isHttpApi = /^http:\/\//i.test(API_BASE);
    const hint =
      error?.name === "TypeError" && isHttpsPage && isHttpApi
        ? "Falha de conexão: o frontend HTTPS não pode chamar a API HTTP (mixed content). Rebuild com REACT_APP_API_BASE=https://brengenhariaconsultiva.com.br"
        : error?.name === "TypeError"
          ? `Isto não é senha nem login errado. O navegador não completou a chamada: ${error?.message || error}`
          : `Isto não é senha nem login errado. ${error?.message || error}`;
    throw new Error(hint);
  }

  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    throw new Error("O servidor não respondeu um login válido. Tente de novo.");
  }

  if (!response.ok) {
    const d = data?.detail;
    const msg =
      typeof d === "string"
        ? d
        : Array.isArray(d)
          ? d.map((x) => x?.msg || x).join("; ")
          : d?.message || "Erro no login";
    throw new Error(msg);
  }

  if (data.access_token) {
    setToken(data.access_token);
  }

  return data.access_token;
}

export async function getMe() {

  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/users/me`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Not authenticated");
  }

  return data;
}

export async function getStats() {

  const token = getToken();
  let response;

  try {
    response = await fetch(`${API_BASE}/api/v1/stats`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    const hint =
      error?.name === "TypeError"
        ? "Falha de conexão com a API (Dashboard). Verifique backend/CORS."
        : `Falha ao buscar dashboard: ${error?.message || error}`;
    throw new Error(hint);
  }

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    if (!response.ok) {
      throw new Error(`Erro HTTP ${response.status} ao buscar dashboard`);
    }
    throw new Error("Resposta inválida da API para dashboard");
  }

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar stats");
  }

  return data;
}

async function _fetchStatsLayer(path, errorLabel) {
  const token = getToken();
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (error) {
    const hint =
      error?.name === "TypeError"
        ? `Falha de conexão com a API (${errorLabel}). Verifique backend/CORS.`
        : `Falha ao buscar ${errorLabel}: ${error?.message || error}`;
    throw new Error(hint);
  }
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    if (!response.ok) {
      throw new Error(`Erro HTTP ${response.status} ao buscar ${errorLabel}`);
    }
    throw new Error(`Resposta inválida da API para ${errorLabel}`);
  }
  if (!response.ok) {
    throw new Error(parseApiError(response, data, `Erro ao buscar ${errorLabel}`));
  }
  return data;
}

/** GET /api/v1/stats/core — KPIs operacionais (camada rápida). */
export async function getStatsCore() {
  return _fetchStatsLayer("/api/v1/stats/core", "dashboard core");
}

/** GET /api/v1/stats/analytics — tipologia + IBGE (camada analítica). */
export async function getStatsAnalytics() {
  return _fetchStatsLayer("/api/v1/stats/analytics", "dashboard analytics");
}

export async function createUser(payload) {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  const data = await response.json();

  if (!response.ok) {
    let msg = "Erro ao criar usuário";

    if (typeof data?.detail === "string") {
      msg = data.detail;
    } else if (Array.isArray(data?.detail)) {
      msg = data.detail.map((item) => item.msg).join(" | ");
    } else if (data?.detail) {
      msg = JSON.stringify(data.detail);
    }

    throw new Error(msg);
  }

  return data;
}

/**
 * GET /api/v1/users - lista de usuários (RBAC no backend).
 * - admin: retorna todos
 * - demais: retorna apenas o usuário autenticado
 */
export async function listUsers() {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/users`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao listar usuários");
  }

  return data;
}

export async function getUserStats() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/stats`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar estatísticas de usuários");
  }
  return data;
}

export async function getUserById(userId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/${userId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar usuário");
  }
  return data;
}

export async function updateUser(userId, payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/${userId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(parseApiError(response, data, "Erro ao actualizar usuário"));
  }
  return data;
}

export async function resetUserPassword(userId, newPassword) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/${userId}/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ new_password: newPassword })
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(parseApiError(response, data, "Erro ao redefinir senha"));
  }
  return data;
}

export async function updateMyProfile(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/me`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) {
    const msg = parseApiError(response, data, "Erro ao actualizar perfil");
    const err = new Error(msg);
    if (response.status === 422) {
      err.fieldErrors = parseFieldErrors(data);
    }
    throw err;
  }
  return data;
}

export async function changeMyPassword(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/users/me/change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) {
    const msg = parseApiError(response, data, "Erro ao alterar senha");
    const err = new Error(msg);
    if (response.status === 422) {
      err.fieldErrors = parseFieldErrors(data);
    }
    throw err;
  }
  return data;
}

export async function listProperties(params = {}) {
  const token = getToken();

  const clean = {};
  Object.keys(params).forEach((key) => {
    if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
      clean[key] = params[key];
    }
  });

  const qs = new URLSearchParams(clean).toString();

  const response = await fetch(`${API_BASE}/api/v1/properties${qs ? `?${qs}` : ""}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar imóveis");
  }

  return data;
}

export async function getProperty(id) {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/properties/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar imóvel");
  }

  return data;
}

/**
 * GET /api/v1/properties/{id}/pois - Pontos de interesse próximos ao imóvel.
 * @param {number} id - ID do imóvel (Property.id)
 * @param {number} [radius] - Raio em metros (opcional, default 500 no backend)
 * @returns {Promise<{ property_id: number; radius_m: number; pois: Array<any> }>}
 */
export async function getPropertyPois(id, radius) {
  const token = getToken();

  const params = new URLSearchParams();
  if (radius != null) {
    params.set("radius", String(radius));
  }

  const qs = params.toString();

  const response = await fetch(
    `${API_BASE}/api/v1/properties/${id}/pois${qs ? `?${qs}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar POIs");
  }

  return data;
}

/**
 * GET /api/v1/properties/{id}/latest-comparables - Comparáveis mais recentes para visualização em mapa.
 * @param {number} id - ID do imóvel (Property.id)
 * @returns {Promise<{ property_id: number; comparables: Array<any> }>}
 */
export async function getPropertyLatestComparables(id) {
  const token = getToken();

  const response = await fetch(
    `${API_BASE}/api/v1/properties/${id}/latest-comparables`,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar comparáveis");
  }

  return data;
}

/**
 * GET /api/v1/properties/{id}/contexto-socioeconomico — setor censitário IBGE.
 */
export async function getPropertyIbgeContext(id) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/properties/${id}/contexto-socioeconomico`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar contexto IBGE");
  }
  return data;
}

/**
 * GET /api/v1/config/maps — chave Google Maps em runtime (sem auth).
 * @returns {Promise<{ google_maps_key: string|null }>}
 */
export async function getMapsConfig() {
  let response;
  try {
    response = await fetch(`${API_BASE}/api/v1/config/maps`);
  } catch (error) {
    throw new Error("Falha de conexão ao buscar configuração do mapa");
  }

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    if (!response.ok) {
      throw new Error(`Erro HTTP ${response.status} ao buscar configuração do mapa`);
    }
    throw new Error("Resposta inválida da API para configuração do mapa");
  }

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao buscar configuração do mapa");
  }

  return data;
}

/**
 * POST /api/v1/predict - Avaliação de imóvel (AVM).
 * @param {Object} payload - { cidade, bairro, area_total, quartos, banheiros, vagas, tipo, estado?, ... }
 * @returns {Promise<Object>} - { valor_estimado, valor_final, confidence_interval_lower, confidence_interval_upper, ... }
 */
export async function predict(payload) {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/predict`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });

  const data = await response.json();

  if (!response.ok) {
    const msg =
      typeof data?.detail === "string"
        ? data.detail
        : Array.isArray(data?.detail)
          ? data.detail.map((item) => item.msg || item).join(" | ")
          : "Erro ao calcular avaliação";
    throw new Error(msg);
  }

  return data;
}

function nbrApiErrorMessage(response, data, fallback) {
  const detail = typeof data?.detail === "string" ? data.detail : "";
  if (response.status === 404 || detail === "Not Found") {
    return "Não encontrado (404). Reinicie o backend com o código atual (as rotas /api/v1/nbr-avm só existem nessa versão). Confira REACT_APP_API_BASE — padrão http://127.0.0.1:8001.";
  }
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item) => item.msg || JSON.stringify(item)).join(" | ");
  }
  return fallback;
}

/** GET /api/v1/nbr-avm/example — amostra sintética para AVM NBR Enterprise */
export async function getNbrExample(n = 40) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/example?n=${n}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro ao carregar exemplo NBR"));
  return data;
}

/** POST /api/v1/nbr-avm/upload-table — Excel para tabela NBR */
export async function uploadNbrTable(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/upload-table`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro ao importar planilha NBR"));
  return data;
}

/** POST /api/v1/nbr-avm/regression */
export async function nbrRegression(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/regression`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro na regressão NBR"));
  return data;
}

/** POST /api/v1/nbr-avm/predict */
export async function nbrPredict(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/predict`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro na predição NBR"));
  return data;
}

/** POST /api/v1/nbr-avm/quick-evaluate — inputs + amostra do banco + OLS em uma chamada */
export async function nbrQuickEvaluate(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/quick-evaluate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro na avaliação rápida NBR"));
  return data;
}

/** POST /api/v1/nbr-avm/advanced-comparables */
export async function nbrAdvancedComparables(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/advanced-comparables`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro ao buscar comparáveis avançados"));
  return data;
}

/** POST /api/v1/nbr-avm/advanced-evaluate */
export async function nbrAdvancedEvaluate(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-avm/advanced-evaluate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro na avaliação avançada NBR"));
  return data;
}

/** POST /api/v1/nbr-lab/advanced-comparables — laboratório (v1.1 forçado) */
export async function nbrLabAdvancedComparables(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/advanced-comparables`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro ao buscar comparáveis (lab)"));
  return data;
}

/** POST /api/v1/nbr-lab/advanced-evaluate — laboratório (v1.1 forçado) */
export async function nbrLabAdvancedEvaluate(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/advanced-evaluate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  let data = {};
  try {
    data = await response.json();
  } catch {
    /* ignore */
  }
  if (!response.ok) throw new Error(nbrApiErrorMessage(response, data, "Erro na avaliação NBR laboratório"));
  return data;
}

/**
 * POST /api/v1/upload - Upload de arquivo CSV/Excel/JSON (admin/analista).
 * @param {File} file - Arquivo a enviar
 * @returns {Promise<{ total_recebidos: number, total_validos: number, total_salvos: number, erros: string[] }>}
 */
export async function uploadFile(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/api/v1/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro no upload");
  }

  return data;
}

/**
 * POST /api/v1/etl/run - Executa pipeline ETL manualmente (admin/analista).
 * @returns {Promise<Object>} - Estatísticas: extracted, cleaned, normalized, enriched, persisted, duplicates, outliers, errors
 */
export async function runEtl() {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/v1/etl/run`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.detail || "Erro ao executar ETL");
  }

  return data;
}

/**
 * POST /api/v1/reports - Gera e baixa relatório PDF ou Excel.
 * @param {Object} opts - { formato: "pdf"|"excel", property_id?: number, valuation_id?: number }
 * @returns {Promise<void>} - Dispara download do arquivo
 */
export async function downloadReport(opts = {}) {
  const token = getToken();
  const { formato = "pdf", property_id, valuation_id } = opts;

  const response = await fetch(`${API_BASE}/api/v1/reports`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      formato,
      property_id: property_id ?? null,
      valuation_id: valuation_id ?? null
    })
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.detail || "Erro ao gerar relatório");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  const match = disposition && disposition.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : `relatorio_${Date.now()}.${formato === "excel" ? "xlsx" : "pdf"}`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * GET /api/v1/scraping/logs - Lista logs de scraping (backend pode filtrar por RBAC).
 * @param {number} [limit=100] - Limite de registros
 * @returns {Promise<Array>} - Lista de ScrapingLog
 */
export async function getScrapingLogs(limit = 100) {
  const token = getToken();
  const response = await fetch(
    `${API_BASE}/api/v1/scraping/logs?limit=${Math.min(Math.max(1, limit), 100)}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar logs de scraping");
  return data;
}

export async function listScrapingJobs(limit = 50) {
  const token = getToken();
  const response = await fetch(
    `${API_BASE}/api/v1/scraping/jobs?limit=${Math.min(Math.max(1, limit), 200)}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar jobs de scraping");
  return data;
}

export async function getScrapingQueue() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/queue`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar fila de scraping");
  return data;
}

/** POST /api/v1/scraping/jobs — usar mode: "complete" (pipeline avisoInfo virgin). */
export async function createScrapingJob(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) {
    const msg =
      typeof data?.detail === "string"
        ? data.detail
        : Array.isArray(data?.detail)
          ? data.detail.map((item) => item.msg || item).join(" | ")
          : "Erro ao criar job de scraping";
    throw new Error(msg);
  }
  return data;
}

export async function approveScrapingJob(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/approve`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao aprovar job");
  return data;
}

export async function runScrapingJob(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/run`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao executar job");
  return data;
}

/** Preview admin: furar fila de job queued. */
export async function previewForceLaunchJob(jobId) {
  const token = getToken();
  const response = await fetch(
    `${API_BASE}/api/v1/scraping/jobs/${jobId}/force-launch-preview`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.detail || data?.message || data?.error || "Erro no preview da fila");
  }
  return data;
}

/**
 * Lança job queued agora (admin).
 * confirmJump=true quando há outros na fila e o admin confirma no popup.
 * Em 409, `error.detail` traz o corpo (running / confirm_jump_required).
 */
export async function forceLaunchScrapingJob(jobId, { confirmJump = false } = {}) {
  const token = getToken();
  const q = confirmJump ? "?confirm_jump=true" : "?confirm_jump=false";
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/force-launch${q}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data?.detail;
    const msg =
      (typeof detail === "object" && detail?.message) ||
      (typeof detail === "string" && detail) ||
      data?.message ||
      "Erro ao forçar execução";
    const err = new Error(msg);
    err.status = response.status;
    err.detail = typeof detail === "object" ? detail : data;
    throw err;
  }
  return data;
}

export async function cancelScrapingJob(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/cancel`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao cancelar job");
  return data;
}

export async function deleteScrapingJob(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` }
  });
  if (response.status === 204) return true;
  let detail = "Erro ao excluir job";
  try {
    const data = await response.json();
    detail = data?.detail || detail;
  } catch {
    /* resposta vazia */
  }
  if (!response.ok) throw new Error(detail);
  return true;
}

/** GET /api/v1/scraping/jobs/{id}/detail — job + últimas linhas do log (sem segredos). */
export async function getScrapingJobDetail(jobId, tail = 200) {
  const token = getToken();
  const response = await fetch(
    `${API_BASE}/api/v1/scraping/jobs/${jobId}/detail?tail=${tail}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar detalhe do job");
  return data;
}

/** GET /api/v1/scraping/jobs/{id}/progress — progress.json + matrix_live (polling). */
export async function getScrapingJobProgress(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/progress`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar progresso do job");
  return data;
}

/** GET /api/v1/scraping/jobs/{id}/persisted-properties — IDs novos inseridos na base. */
export async function getScrapingJobPersistedProperties(jobId) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/jobs/${jobId}/persisted-properties`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao buscar IDs persistidos");
  return data;
}

/** GET /api/v1/scraping/schedule — painel de agendamento No-Human v4 (admin). */
export async function getScrapingSchedule() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/schedule`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao carregar agendamento"));
  return data;
}

/** PATCH /api/v1/scraping/schedule — guardar configuração de agendamento. */
export async function updateScrapingSchedule(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/schedule`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao guardar agendamento"));
  return data;
}

/** POST /api/v1/scraping/schedule/dispatch — criar job agendado / disparar agora. */
export async function dispatchScrapingSchedule(payload = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/schedule/dispatch`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao disparar agendamento"));
  return data;
}

/** GET /api/v1/scraping/acquisition/preview — prévia IDs novos (listagem SP, admin). */
export async function getScrapingAcquisitionPreview({ discovery_limit = 60, pick_limit = 15 } = {}) {
  const token = getToken();
  const qs = new URLSearchParams({
    discovery_limit: String(discovery_limit),
    pick_limit: String(pick_limit)
  });
  const response = await fetch(`${API_BASE}/api/v1/scraping/acquisition/preview?${qs}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro na prévia de captação"));
  return data;
}

/** GET /api/v1/scraping/acquisition/schedule — calendário de expansão (IDs Novos). */
export async function getScrapingAcquisitionSchedule() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/acquisition/schedule`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao carregar agendamento de expansão"));
  return data;
}

/** PATCH /api/v1/scraping/acquisition/schedule — guardar agendamento de expansão. */
export async function updateScrapingAcquisitionSchedule(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/acquisition/schedule`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao guardar agendamento de expansão"));
  return data;
}

/** POST /api/v1/scraping/acquisition/schedule/dispatch — disparar captação automática. */
export async function dispatchScrapingAcquisitionSchedule(payload = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/scraping/acquisition/schedule/dispatch`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(parseApiError(response, data, "Erro ao disparar captação automática"));
  return data;
}

/** GET /api/v1/features — feature flags efectivas para o utilizador */
export async function fetchFeatures() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/features`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao carregar feature flags");
  return data;
}

/** POST /api/v1/nbr-lab/google-geocode-test/cep-lookup — preenche endereço por CEP (lab) */
export async function nbrLabCepLookup(cep) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/google-geocode-test/cep-lookup`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ cep })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao consultar CEP");
  return data;
}

/** POST /api/v1/nbr-lab/google-geocode-test — teste isolado Google Geocoding (lab) */
export async function nbrLabGoogleGeocodeTest(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/google-geocode-test`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro no teste de geocodificação");
  return data;
}

/** POST /api/v1/geo-intelligence/cep/resolve — Cadastro Inteligente V2 */
export async function geoIntelligenceCepResolve(cep) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/geo-intelligence/cep/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ cep })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao consultar CEP");
  return data;
}

/** POST /api/v1/geo-intelligence/locate — geocodifica e devolve GeoContext */
export async function geoIntelligenceLocate(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/geo-intelligence/locate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao localizar endereço");
  return data;
}

/** GET /api/v1/geo-intelligence/municipios?uf=SP */
export async function geoIntelligenceMunicipios(uf) {
  const token = getToken();
  const response = await fetch(
    `${API_BASE}/api/v1/geo-intelligence/municipios?uf=${encodeURIComponent(uf)}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao listar municípios");
  return data;
}

/** POST /api/v1/nbr-lab/inteligencia-geografica/cep/resolve — Inteligência Geográfica V1 */
export async function nbrLabInteligenciaGeograficaCepResolve(cep) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/inteligencia-geografica/cep/resolve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ cep })
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao consultar CEP");
  return data;
}

/** POST /api/v1/nbr-lab/inteligencia-geografica/locate — GeoContext + Diagnóstico V1 */
export async function nbrLabInteligenciaGeograficaLocate(payload) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/inteligencia-geografica/locate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Erro ao validar localização");
  return data;
}

/** GET /api/v1/nbr-lab/access — verifica acesso ao laboratório (SUPER_ADMIN) */
export async function fetchNbrLabAccess() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/nbr-lab/access`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.detail || "Acesso negado ao laboratório");
  return data;
}

// --- Pipeline Scraping V2 — Bairros (Fase 4.1) ---

async function _bairroPipelineFetch(path, options = {}) {
  const token = getToken();
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers
      }
    });
  } catch (error) {
    const isHttpsPage =
      typeof window !== "undefined" && window.location?.protocol === "https:";
    const isHttpApi = /^http:\/\//i.test(API_BASE);
    if (error?.name === "TypeError" && isHttpsPage && isHttpApi) {
      throw new Error(
        "Falha de conexão: o frontend HTTPS não pode chamar a API HTTP (mixed content). Rebuild com REACT_APP_API_BASE=https://brengenhariaconsultiva.com.br"
      );
    }
    throw new Error(
      "Falha de conexão com a API (HTTPS). Se o Gravar/START falhou agora, espera 10 s e tenta outra vez — a API pode ter reiniciado."
    );
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseApiError(response, data, "Erro na API do pipeline por bairros"));
  }
  return data;
}

/** RQ-1000 lab — Super Admin only */
export async function getScrapeGeralLabStatus() {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/status`);
}

export async function postScrapeGeralLabConfig(body) {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/config`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

export async function postScrapeGeralLabControl(desired) {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/control`, {
    method: "POST",
    body: JSON.stringify({ desired })
  });
}

export async function postScrapeGeralLabPanic() {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/panic`, {
    method: "POST"
  });
}

export async function postScrapeGeralLabDiagnostic() {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/diagnostic`, {
    method: "POST"
  });
}

export async function postScrapeGeralLabRecover(action) {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/recover`, {
    method: "POST",
    body: JSON.stringify({ action })
  });
}

export async function getScrapeGeralLabCollectCf() {
  return _bairroPipelineFetch(`/api/v1/scraping/scrape-geral-lab/collect-cf`);
}

/** GET /api/v1/scraping/bairros/catalog — catálogo (~300) + stats do banco numa chamada */
export async function getBairroCatalog() {
  return _bairroPipelineFetch(`/api/v1/scraping/bairros/catalog`);
}

/** GET /api/v1/scraping/bairros/schedules — agendamentos por bairro + presets de cron */
export async function listBairroSchedules() {
  return _bairroPipelineFetch(`/api/v1/scraping/bairros/schedules`);
}

/** POST /api/v1/scraping/bairros/schedules — cria agendamento (once/recurring) */
export async function createBairroSchedule(body = {}) {
  return _bairroPipelineFetch(`/api/v1/scraping/bairros/schedules`, {
    method: "POST",
    body: JSON.stringify(body)
  });
}

/** DELETE /api/v1/scraping/bairros/schedules/{id} */
export async function deleteBairroSchedule(scheduleId) {
  return _bairroPipelineFetch(`/api/v1/scraping/bairros/schedules/${encodeURIComponent(scheduleId)}`, {
    method: "DELETE"
  });
}

/** GET /api/v1/scraping/bairros/{slug}/pipeline-summary/pointer */
export async function getBairroPipelinePointer(bairroSlug) {
  return _bairroPipelineFetch(
    `/api/v1/scraping/bairros/${encodeURIComponent(bairroSlug)}/pipeline-summary/pointer`
  );
}

/** GET /api/v1/scraping/bairros/{slug}/pipeline-summary/latest */
export async function getBairroPipelineLatestSummary(bairroSlug) {
  return _bairroPipelineFetch(
    `/api/v1/scraping/bairros/${encodeURIComponent(bairroSlug)}/pipeline-summary/latest`
  );
}

/** POST /api/v1/scraping/bairros/{slug}/pipeline/runs */
export async function createBairroPipelineRun(bairroSlug, body = {}) {
  return _bairroPipelineFetch(
    `/api/v1/scraping/bairros/${encodeURIComponent(bairroSlug)}/pipeline/runs`,
    {
      method: "POST",
      body: JSON.stringify(body)
    }
  );
}

/** GET /api/v1/scraping/bairros/pipeline/runs/{jobId}/progress */
export async function getBairroPipelineRunProgress(jobId) {
  return _bairroPipelineFetch(`/api/v1/scraping/bairros/pipeline/runs/${jobId}/progress`);
}

// --- Geo SP Super Tabela (96 distritos) ---

async function _geoSpFetch(path, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseApiError(response, data, "Erro na API Geo SP"));
  }
  return data;
}

/** GET /api/v1/geo-sp/distritos */
export async function getGeoSpDistritos(params = {}) {
  const qs = new URLSearchParams();
  if (params.zona_macro) qs.set("zona_macro", params.zona_macro);
  if (params.modules) qs.set("modules", params.modules);
  if (params.q) qs.set("q", params.q);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.view) qs.set("view", params.view);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return _geoSpFetch(`/api/v1/geo-sp/distritos${suffix}`);
}

/** GET /api/v1/geo-sp/distritos/{slug} */
export async function getGeoSpDistrito(slug, params = {}) {
  const qs = new URLSearchParams();
  if (params.modules) qs.set("modules", params.modules);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return _geoSpFetch(`/api/v1/geo-sp/distritos/${encodeURIComponent(slug)}${suffix}`);
}

/** GET /api/v1/geo-sp/distritos/{slug}/bairros — populares + catálogo + PostGIS */
export async function getGeoSpDistritoBairros(slug) {
  return _geoSpFetch(`/api/v1/geo-sp/distritos/${encodeURIComponent(slug)}/bairros`);
}

/** GET /api/v1/geo-sp/zonas */
export async function getGeoSpZonas() {
  return _geoSpFetch(`/api/v1/geo-sp/zonas`);
}

/** GET /api/v1/geo-sp/map/geojson */
export async function getGeoSpMapGeojson() {
  return _geoSpFetch(`/api/v1/geo-sp/map/geojson`);
}

/** GET /api/v1/geo-sp/scrape-plan — planeamento de scrape por zona/subprefeitura */
export async function getGeoSpScrapePlan(params = {}) {
  const qs = new URLSearchParams();
  if (params.zona_macro) qs.set("zona_macro", params.zona_macro);
  if (params.subprefeitura_codigo != null) qs.set("subprefeitura_codigo", String(params.subprefeitura_codigo));
  const q = qs.toString();
  return _geoSpFetch(`/api/v1/geo-sp/scrape-plan${q ? `?${q}` : ""}`);
}

/** POST /api/v1/admin/cf-manual/start — validação CF noVNC (admin) */
export async function startCfManualVelox(body = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/admin/cf-manual/start`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || "Falha ao iniciar CF manual");
  }
  return data;
}

/** GET /api/v1/admin/cf-manual/status */
export async function getCfManualStatus() {
  const token = getToken();
  const response = await fetch(`${API_BASE}/api/v1/admin/cf-manual/status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || "Falha ao consultar CF manual");
  }
  return data;
}

/** ——— Valuation Enterprise V1 (B1) — isolado; flag VALUATION_ENTERPRISE_V1 ——— */

async function valuationEnterpriseFetch(path, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : detail?.message || data?.message || `Erro Valuation Enterprise (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.detail = detail;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function valuationEnterpriseAccess() {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/access");
}

export async function valuationEnterpriseCepResolve(cep) {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/cep/resolve", {
    method: "POST",
    body: JSON.stringify({ cep })
  });
}

export async function valuationEnterpriseAddressSuggest(payload) {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/address/suggest", {
    method: "POST",
    body: JSON.stringify(payload || {})
  });
}

export async function valuationEnterpriseLocate(payload) {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/locate", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function valuationEnterpriseMunicipios(uf) {
  return valuationEnterpriseFetch(
    `/api/v1/valuation-enterprise/municipios?uf=${encodeURIComponent(uf)}`
  );
}

export async function valuationEnterpriseCreateDraft(payload) {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/drafts", {
    method: "POST",
    body: JSON.stringify({ payload })
  });
}

export async function valuationEnterpriseSaveDraft(draftId, payload, opts = {}) {
  const body = { payload };
  if (opts.expectedVersion != null) body.expected_version = opts.expectedVersion;
  return valuationEnterpriseFetch(`/api/v1/valuation-enterprise/drafts/${encodeURIComponent(draftId)}`, {
    method: "PUT",
    body: JSON.stringify(body)
  });
}

export async function valuationEnterpriseGetDraft(draftId) {
  return valuationEnterpriseFetch(`/api/v1/valuation-enterprise/drafts/${encodeURIComponent(draftId)}`);
}

export async function valuationEnterpriseListDrafts() {
  return valuationEnterpriseFetch("/api/v1/valuation-enterprise/drafts");
}


/** ——— Valuation Automatic V1 (RQ-093) — reexport client isolado (RQ-103 build limpo) ——— */
export {
  valuationAutomaticAccess,
  valuationAutomaticCepResolve,
  valuationAutomaticAddressSuggest,
  valuationAutomaticLocate,
  valuationAutomaticMunicipios,
  valuationAutomaticCreateDraft,
  valuationAutomaticSaveDraft,
  valuationAutomaticGetDraft,
  valuationAutomaticListDrafts,
  valuationAutomaticIptuLookup
} from "./valuationAutomaticCoreClient_v1";

/** ——— Message Center (RQ-102A) — caixa de mensagens + contatos + aprovação pendente ——— */

async function _messagesFetch(path, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseApiError(response, data, "Erro na Caixa de Mensagens"));
  }
  return data;
}

/** GET /api/v1/messages/inbox */
export async function getMessagesInbox(limit = 200) {
  return _messagesFetch(`/api/v1/messages/inbox?limit=${limit}`);
}

/** GET /api/v1/messages/sent */
export async function getMessagesSent(limit = 200) {
  return _messagesFetch(`/api/v1/messages/sent?limit=${limit}`);
}

/** POST /api/v1/messages/send — { to_user_id? , to_username?, subject?, body } */
export async function sendDirectMessage(payload) {
  return _messagesFetch(`/api/v1/messages/send`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

/** POST /api/v1/messages/{id}/read */
export async function markMessageRead(messageId) {
  return _messagesFetch(`/api/v1/messages/${messageId}/read`, { method: "POST" });
}

/** GET /api/v1/messages/contacts */
export async function getMessageContacts() {
  return _messagesFetch(`/api/v1/messages/contacts`);
}

/** POST /api/v1/messages/contacts — { contact_user_id? , username? } */
export async function createMessageContact(payload) {
  return _messagesFetch(`/api/v1/messages/contacts`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

/** POST /api/v1/messages/contacts/{id}/accept */
export async function acceptMessageContact(contactId) {
  return _messagesFetch(`/api/v1/messages/contacts/${contactId}/accept`, { method: "POST" });
}

/** POST /api/v1/messages/contacts/{id}/block */
export async function blockMessageContact(contactId) {
  return _messagesFetch(`/api/v1/messages/contacts/${contactId}/block`, { method: "POST" });
}

/** POST /api/v1/messages/contacts/{id}/delete */
export async function deleteMessageContact(contactId) {
  return _messagesFetch(`/api/v1/messages/contacts/${contactId}/delete`, { method: "POST" });
}

/** GET /api/v1/messages/users/search?q= — busca leve p/ compor mensagem ou adicionar contato */
export async function searchMessageUsers(q) {
  return _messagesFetch(`/api/v1/messages/users/search?q=${encodeURIComponent(q)}`);
}

/** GET /api/v1/messages/admin/pending-registrations — SUPER_ADMIN */
export async function getPendingRegistrations() {
  return _messagesFetch(`/api/v1/messages/admin/pending-registrations`);
}

/** POST /api/v1/messages/admin/pending-registrations/{id}/approve — SUPER_ADMIN */
export async function approvePendingRegistration(userId) {
  return _messagesFetch(`/api/v1/messages/admin/pending-registrations/${userId}/approve`, {
    method: "POST"
  });
}

/** POST /api/v1/messages/admin/pending-registrations/{id}/reject — SUPER_ADMIN */
export async function rejectPendingRegistration(userId, reason) {
  return _messagesFetch(`/api/v1/messages/admin/pending-registrations/${userId}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason: reason || null })
  });
}

/** RQ-105B.2 — Painel Super Admin · Comunicação */
async function _adminCommsFetch(path, options = {}) {
  const token = getToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(parseApiError(data) || data?.detail || "Erro na API de comunicação");
  }
  return data;
}

export async function getAdminCommsOverview() {
  return _adminCommsFetch(`/api/v1/admin/comms/overview`);
}

export async function getAdminCommsOutbox(params = {}) {
  const q = new URLSearchParams();
  if (params.recipient) q.set("recipient", params.recipient);
  if (params.event) q.set("event", params.event);
  if (params.status) q.set("status", params.status);
  if (params.date_from) q.set("date_from", params.date_from);
  if (params.date_to) q.set("date_to", params.date_to);
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return _adminCommsFetch(`/api/v1/admin/comms/outbox${qs ? `?${qs}` : ""}`);
}

export async function processAdminCommsOutbox(limit = 20) {
  return _adminCommsFetch(`/api/v1/admin/comms/outbox/process`, {
    method: "POST",
    body: JSON.stringify({ limit })
  });
}
